import { Guid } from '@constellation-mutual/guid';
import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import Auth from '@/services/Auth';
import store from '@/store';
import { ProviderProfile } from './Providers';

export enum CoverageType {
     clinic = 'clinic',
     individual = 'individual',
}

export enum PolicyType {
     claimsMade = 'claims-made',
     occurrence = 'occurrence',
}

export interface QuoteTerm {
     premiumDollars: number;
     startDate: string;
     stopDateExclusive: string;
}

export interface QuoteRate {
     liabilityLimits: {
          aggregateDollars: number;
          incidentDollars: number;
     };
     terms: QuoteTerm[];
}

export class Quote {
     public constructor(data: Quote) {
          Object.assign(this, data);
     }

     public applicationId?: Guid;
     public coverageScope: {
          clinicPriorActsDate: string;
          coverageType: CoverageType;
          providerProfiles: ProviderProfile[];
     };
     public id: Guid;
     public policyParams: {
          policyType: PolicyType;
          startDate: string;
     };
     public practice: {
          region: {
               state: string;
          };
     };
     public rates?: {
          rateIndex: number;
          rates: QuoteRate[];
     };
     public timestampCreated: string;

     public static mock(): Quote {
          return new Quote({
               coverageScope: {
                    clinicPriorActsDate: '2019/01/01',
                    coverageType: CoverageType.clinic,
                    providerProfiles: [ProviderProfile.mock(), ProviderProfile.mock()],
               },
               id: '8a2b9eb4-f693-493c-8f7e-2c82c99e20f3',
               policyParams: {
                    policyType: PolicyType.claimsMade,
                    startDate: '2019/06/01',
               },
               practice: {
                    region: {
                         state: 'Minnesota',
                    },
               },
               rates: {
                    rateIndex: 0,
                    rates: [
                         {
                              liabilityLimits: {
                                   aggregateDollars: 4000000,
                                   incidentDollars: 2000000,
                              },
                              terms: [
                                   {
                                        premiumDollars: 1234,
                                        startDate: '2019/06/01',
                                        stopDateExclusive: '2020/06/01',
                                   },
                              ],
                         },
                    ],
               },
               timestampCreated: '2019-03-10T23:41:54.669Z',
          });
     }

     public static getRateIndex(quote: Quote): number {
          let rateIndex = Math.max(0, quote.rates.rateIndex);
          rateIndex = Math.min(quote.rates.rates.length - 1, quote.rates.rateIndex);
          return rateIndex;
     }

     public static getRate(quote: Quote): QuoteRate {
          return quote.rates.rates[Quote.getRateIndex(quote)];
     }
}

export class QuotesService {
     public constructor(private readonly _baseUrl: string) {}

     public async readQuote(quoteId: Guid): Promise<Quote | undefined> {
          const validQuoteId: Guid = Guid.schema.validateSync(quoteId);
          const response: AxiosResponse = await axios.get(
               `/users/current/quotes/${validQuoteId}`,
               this.createAxiosRequestConfig(),
          );
          return response.data && new Quote(response.data);
     }

     public async readQuotesForUserId(_userId: Guid): Promise<Quote[]> {
          const response: AxiosResponse = await axios.get(
               '/users/current/quotes',
               this.createAxiosRequestConfig(),
          );

          return Array.isArray(response.data)
               ? response.data
                      .map(item => new Quote(item))
                      // Sort newest first.
                      .sort(
                           (a: Quote, b: Quote): number =>
                                new Date(b.timestampCreated).getTime() -
                                new Date(a.timestampCreated).getTime(),
                      )
               : [];
     }

     public async getBrokerQuotes(): Promise<Quote[]> {
          const response: AxiosResponse = await axios.get(
               '/brokerage/current/quotes/' + store.state.Auth.brokerageId,
               this.createAxiosRequestConfig(),
          );

          return Array.isArray(response.data)
               ? response.data
                      .map(item => new Quote(item))
                      .sort(
                           (a, b) =>
                                new Date(b.timestampCreated).getTime() -
                                new Date(a.timestampCreated).getTime(),
                      )
               : [];
     }

     public async resumeQuote(quoteId): Promise<void> {
          const targetUrl = new URL(process.env.VUE_APP_DAX_WEBSITE_QUOTES_RESUME_URI);
          targetUrl.searchParams.set('quoteId', quoteId);
          await Auth.setSessionTransfer(targetUrl.toString());
     }

     public async newQuote(): Promise<void> {
          await Auth.setSessionTransfer(process.env.VUE_APP_DAX_WEBSITE_QUOTES_RESUME_URI);
     }

     private createAxiosRequestConfig(): AxiosRequestConfig {
          const config: AxiosRequestConfig = {
               baseURL: this._baseUrl,
          };
          return config;
     }
}

const Quotes = new QuotesService(process.env.VUE_APP_DAX_API_URI);
export default Quotes;
